import { useMemo, useState } from "react";
import PageLayout from "@/components/feature/PageLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/feature/EmptyState";
import MovieFilters from "@/pages/movies/components/MovieFilters";
import Pagination from "@/pages/movies/components/Pagination";
import { movies } from "@/mocks/movies";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useIsDesktop } from "@/hooks/useIsDesktop";

const PAGE_SIZE = 8;

export default function Movies() {
  usePageMeta(
    "午夜视频 - 午夜电影分类与精选推荐",
    "午夜视频电影分类页，按类型、地区、年份筛选高清午夜电影，支持上映时间与热度排序，快速找到今晚想看的那一部。",
    "午夜电影分类,高清午夜电影,悬疑电影,恐怖电影,午夜视频电影库",
  );

  const isDesktop = useIsDesktop();
  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部地区");
  const [year, setYear] = useState("全部年份");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = movies.filter((movie) => {
      const genreOk = genre === "全部" || movie.genre === genre;
      const regionOk = region === "全部地区" || movie.region === region;
      const yearOk = year === "全部年份" || String(movie.year) === year;
      return genreOk && regionOk && yearOk;
    });

    return [...list].sort((a, b) => {
      if (sort === "heat") return b.heat - a.heat;
      if (sort === "classic") return a.year - b.year;
      return a.releaseDate < b.releaseDate ? 1 : -1;
    });
  }, [genre, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visible = isDesktop
    ? filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)
    : filtered.slice(0, safePage * PAGE_SIZE);

  const handleFilterChange = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  return (
    <PageLayout>
      <div className="w-full px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb items={[{ label: "电影分类" }]} />

        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="flex items-center gap-2 font-label text-xs tracking-[0.3em] text-accent-500">
              <span className="inline-block h-px w-6 bg-accent-500/70" />
              MOVIE LIBRARY
            </p>
            <h1 className="mt-3 font-heading text-2xl font-bold tracking-wide text-foreground-50 md:text-3xl">
              午夜电影精选
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-400">
              按类型、地区与年份逐层筛选，找到适合今晚气氛的片子。所有条目均附导演、主演与剧情简介，可进入详情页收藏或分享。
            </p>
          </div>
          <div className="flex items-center gap-4 rounded-lg border border-background-800 bg-background-900/60 px-4 py-3">
            <div>
              <p className="font-label text-xl text-accent-400">{movies.length}</p>
              <p className="mt-0.5 text-[11px] text-foreground-500">在库影片</p>
            </div>
            <span className="h-8 w-px bg-background-700" />
            <div>
              <p className="font-label text-xl text-secondary-200">7</p>
              <p className="mt-0.5 text-[11px] text-foreground-500">影片类型</p>
            </div>
            <span className="h-8 w-px bg-background-700" />
            <div>
              <p className="font-label text-xl text-secondary-200">4</p>
              <p className="mt-0.5 text-[11px] text-foreground-500">出品地区</p>
            </div>
          </div>
        </div>

        <div className="mt-7">
          <MovieFilters
            genre={genre}
            region={region}
            year={year}
            sort={sort}
            resultCount={filtered.length}
            onGenreChange={handleFilterChange(setGenre)}
            onRegionChange={handleFilterChange(setRegion)}
            onYearChange={handleFilterChange(setYear)}
            onSortChange={handleFilterChange(setSort)}
            onReset={() => {
              setGenre("全部");
              setRegion("全部地区");
              setYear("全部年份");
              setSort("newest");
              setPage(1);
            }}
          />
        </div>

        <div className="mt-6">
          {filtered.length === 0 ? (
            <EmptyState
              icon="ri-film-line"
              title="这个组合下暂时没有影片"
              description="试着放宽筛选条件，比如换一个年份或地区，也可以回到全部分类重新挑选。"
              actionLabel="返回电影列表"
              actionTo="/movies"
            />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-4">
                {visible.map((movie, index) => (
                  <div
                    key={movie.id}
                    className="animate-rise"
                    style={{ animationDelay: `${Math.min(index, 8) * 50}ms` }}
                  >
                    <MovieCard
                      id={movie.id}
                      title={movie.title}
                      year={movie.year}
                      genre={movie.genre}
                      region={movie.region}
                      director={movie.director}
                      duration={movie.duration}
                      poster={movie.poster}
                    />
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-col items-center gap-4">
                <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
                <p className="hidden text-xs text-foreground-500 md:block">
                  第 {safePage} / {totalPages} 页 · 每页 {PAGE_SIZE} 部
                </p>
                {safePage * PAGE_SIZE < filtered.length && (
                  <button
                    type="button"
                    onClick={() => setPage(safePage + 1)}
                    className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-700 bg-background-900 px-5 py-3.5 text-sm text-foreground-100 transition-colors hover:border-accent-500/60 hover:text-accent-400 md:hidden"
                  >
                    <i className="ri-add-line text-base" />
                    加载更多影片
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
}